import React, { useState } from 'react';
import { Button, Modal, Steps } from 'antd';
import PolicyPrivilegeForm from './PolicyPrivilegeForm';
import PolicyTypeForm from './PolicyTypeForm';
import PolicyActorForm from './PolicyActorForm';
import { ActorFilter, Policy, PolicyType, ResourceFilter } from '../../../types.generated';
import { EMPTY_POLICY } from './policyUtils';
import { useEnterKeyListener } from '../../shared/useEnterKeyListener';
import ClickOutside from '../../shared/ClickOutside';
import { useTranslation } from '../../../i18n/useTranslation';

type Props = {
    policy: Omit<Policy, 'urn'>;
    policyName?: string;
    setPolicy: (policy: Omit<Policy, 'urn'>) => void;
    visible: boolean;
    onClose: () => void;
    onSave: (savePolicy: Omit<Policy, 'urn'>) => void;
};

/**
 * Component used for constructing new policies. The purpose of this flow is to populate or edit a Policy
 * object through a sequence of steps.
 */
export default function PolicyBuilderModal({ policy, setPolicy, visible, onClose, onSave, policyName }: Props) {
    const { t } = useTranslation();

    // Step control-flow.
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    // Go to next step
    const next = () => {
        setActiveStepIndex(activeStepIndex + 1);
    };

    // Go to previous step
    const prev = () => {
        setActiveStepIndex(activeStepIndex - 1);
    };

    // Save or create a policy
    const onSavePolicy = () => {
        onSave(policy);
    };

    // Change the type of policy, either Metadata or Platform
    const setPolicyType = (type: PolicyType) => {
        // Important: If the policy type itself is changing, we need to clear policy state.
        if (type === PolicyType.Platform) {
            setPolicy({ ...policy, type, resources: EMPTY_POLICY.resources, privileges: [] });
        }
        setPolicy({ ...policy, type, privileges: [] });
    };

    // Step 1: Choose Policy Type
    const typeStep = () => {
        return {
            title: t('Choose Policy Type'),
            content: (
                <PolicyTypeForm
                    policyType={policy.type}
                    setPolicyType={(type: PolicyType) => setPolicyType(type)}
                    policyName={policy.name}
                    setPolicyName={(name: string) => setPolicy({ ...policy, name })}
                    policyDescription={policy.description || ''}
                    setPolicyDescription={(description: string) => setPolicy({ ...policy, description })}
                />
            ),
            complete: policy.type && policy.name && policy.name.trim().length > 0, // Whether the "next" button should appear.
        };
    };

    // Step 2: Select privileges step.
    const privilegeStep = () => ({
        title: t('Configure Privileges'),
        content: (
            <PolicyPrivilegeForm
                policyType={policy.type}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                resources={policy.resources!}
                setResources={(resources: ResourceFilter) => {
                    setPolicy({ ...policy, resources });
                }}
                privileges={policy.privileges}
                setPrivileges={(privileges: string[]) => setPolicy({ ...policy, privileges })}
            />
        ),
        complete: policy.privileges && policy.privileges.length > 0, // Whether the "next" button should appear.
    });

    // Step 3: Assign Actors Step
    const actorStep = () => {
        return {
            title: t('Assign Users & Groups'),
            content: (
                <PolicyActorForm
                    policyType={policy.type}
                    actors={policy.actors}
                    setActors={(actors: ActorFilter) =>
                        setPolicy({
                            ...policy,
                            actors,
                        })
                    }
                />
            ),
            complete: true, // Whether the "next" button should appear.
        };
    };

    // Construct final set of steps.
    const policySteps = [typeStep(), privilegeStep(), actorStep()];

    // Get active step.
    const activeStep = policySteps[activeStepIndex];

    const actionButtons = (
        <>
            {activeStepIndex > 0 && (
                <Button type="default" className="ant-btn-default" onClick={() => prev()}>
                    {t('Previous')}
                </Button>
            )}
            {activeStepIndex < policySteps.length - 1 && activeStep.complete && (
                <Button type="primary" onClick={() => next()}>
                    {t('Next')}
                </Button>
            )}
            {activeStepIndex === policySteps.length - 1 && activeStep.complete && (
                <Button type="primary" onClick={onSavePolicy}>
                    {t('Save')}
                </Button>
            )}
        </>
    );
    useEnterKeyListener({
        querySelectorToExecuteClick: '#nextButton',
    });

    useEnterKeyListener({
        querySelectorToExecuteClick: '#saveButton',
    });

    // modalClosePopup for outside policy modal click
    const modalClosePopup = () => {
        Modal.confirm({
            title: t('Exit Policy Editor'),
            content: t('Are you sure you want to exit policy editor? All changes will be lost'),
            onOk() {
                onClose();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            maskClosable: true,
            closable: true,
            centered: true,
        });
    };

    return (
        <ClickOutside onClickOutside={modalClosePopup} wrapperClassName="PolicyBuilderModal">
            <Modal
                wrapClassName="PolicyBuilderModal"
                title={!policyName ? t('Create a new Policy') : t('Edit {{policyName}}', { policyName })}
                visible={visible}
                onCancel={onClose}
                closable
                className="f-modal"
                centered
                footer={actionButtons}
            >
                <Steps current={activeStepIndex}>
                    {policySteps.map((item) => (
                        <Steps.Step key={item.title} title={item.title} />
                    ))}
                </Steps>
                <div className="steps-content mt-4 px-3">{activeStep.content}</div>
            </Modal>
        </ClickOutside>
    );
}
